
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { RiskQuestion } from './RiskQuestion';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import type { Application } from '@/types/database.types';

interface RiskAssessmentProps {
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
}

export const RiskAssessment = ({ register, setValue, watch }: RiskAssessmentProps) => {
  const hasHeightsWork = watch('has_heights_work') as boolean | undefined;
  const hasBelowGroundWork = watch('has_below_ground_work') as boolean | undefined;
  const hasVehicleExposure = watch('has_vehicle_exposure') as boolean | undefined;
  const hasHazardousMaterials = watch('has_hazardous_materials') as boolean | undefined;
  const hasForeignTravel = watch('has_foreign_travel') as boolean | undefined;
  const hasDefenseBaseAct = watch('has_defense_base_act') as boolean | undefined;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Operational Specifics</h3>
      
      {/* Safety Program */}
      <RiskQuestion
        id="has_safety_program"
        label="Does the organization have a formal safety program?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="safety_program_details"
        detailsPlaceholder="Please describe the safety program, including training procedures, incident reporting, and safety protocols..."
      />
      
      {/* Vehicle Exposure - moved above over-road trucking and updated text */}
      <div className="flex items-center justify-between">
        <Label htmlFor="has_vehicle_exposure" className="flex-1">
          Does the organization have employees who drive for job duties including personal or organization-owned vehicles?
        </Label>
        <Switch
          id="has_vehicle_exposure"
          checked={hasVehicleExposure || false}
          onCheckedChange={(checked) => {
            setValue('has_vehicle_exposure', checked);
          }}
        />
      </div>
      
      {hasVehicleExposure && (
        <div className="space-y-4 pl-5 border-l-2 border-primary/20">
          <div className="space-y-2">
            <Label htmlFor="vehicle_count">Number of Vehicles Operated</Label>
            <Input
              id="vehicle_count"
              {...register('vehicle_count')}
              type="text"
              placeholder="Enter the number of vehicles"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="driver_count">Number of Employees Who Drive</Label>
            <Input
              id="driver_count"
              {...register('driver_count')}
              type="text"
              placeholder="Enter the number of drivers"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="vehicle_safety_program">Vehicle Safety Program Details</Label>
            <Textarea
              id="vehicle_safety_program"
              {...register('vehicle_safety_program')}
              placeholder="Describe the vehicle safety program, driver training, vehicle maintenance, and fleet management policies..."
            />
          </div>
        </div>
      )}
      
      {/* Over-road Trucking - moved below vehicle exposure */}
      <RiskQuestion
        id="has_over_road_trucking"
        label="Does the organization have over-the-road trucking exposure?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="over_road_trucking_details"
        detailsPlaceholder="Please describe the trucking operations, fleet size, types of vehicles, driver qualifications, and safety programs..."
      />
      
      {/* Cannabis/Hemp Contact */}
      <RiskQuestion
        id="has_cannabis_hemp_contact"
        label="Does the organization have cannabis or hemp contact?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="cannabis_hemp_details"
        detailsPlaceholder="Please describe the organization's involvement with cannabis or hemp, including cultivation, processing, or distribution activities..."
      />
      
      {/* Workplace Risk */}
      <RiskQuestion
        id="has_elevated_workplace_risk"
        label="Does the organization have elevated workplace risks?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="elevated_workplace_risk_details"
        detailsPlaceholder="Please describe the elevated workplace risks, including specific hazards, safety measures, and risk mitigation strategies..."
      />

      {/* Physical Stock */}
      <RiskQuestion
        id="has_physical_stock"
        label="Does the organization maintain physical inventory/stock at any time?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="physical_stock_details"
        detailsPlaceholder="Please describe the type of inventory, storage methods, handling procedures, and any associated risks..."
      />
      
      {/* Engineering Services */}
      <RiskQuestion
        id="provides_engineering_services"
        label="Does the organization provide any engineering or architectural services that involves work away from an office environment (like a site inspection)?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="engineering_services_details"
        detailsPlaceholder="Please describe the types of engineering services provided, project complexity, and professional liability measures..."
      />
      
      {/* Combined USL&H/Jones Act and Marine Operations */}
      <RiskQuestion
        id="has_usl_jones_exposure"
        label="Does the organization have any USL&H exposure (work on water, docks, or barges) or Jones Act exposure (work on navigable waters)?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="usl_jones_exposure_details"
        detailsPlaceholder="Please describe the maritime operations, vessel types, dock work, longshore activities, and any water-related work..."
      />
      
      {/* Aircraft Operation */}
      <RiskQuestion
        id="has_company_aircraft"
        label="Does the organization own, lease, or operate aircraft for business use?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="company_aircraft_details"
        detailsPlaceholder="Please describe the aircraft types, pilot qualifications, flight operations, and aviation safety programs..."
      />
      
      {/* Heavy Machinery */}
      <RiskQuestion
        id="has_heavy_machinery"
        label="Does the organization utilize heavy machinery?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="heavy_machinery_details"
        detailsPlaceholder="Please describe the types of heavy machinery, operator training requirements, maintenance programs, and safety protocols..."
      />

      {/* Garment Manufacturing in California */}
      <RiskQuestion
        id="has_garment_manufacturing_ca"
        label="Does the organization perform any garment manufacturing in California?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="garment_manufacturing_ca_details"
        detailsPlaceholder="Please describe the garment manufacturing operations, including types of garments produced, number of workers involved, compliance with California labor laws, and any subcontracting arrangements..."
      />

      {/* Defense Base Act */}
      <div className="flex items-center justify-between">
        <Label htmlFor="has_defense_base_act" className="flex-1">
          Does the organization have Defense Base Act exposure (employees who work on U.S. military bases outside the United States)?
        </Label>
        <Switch
          id="has_defense_base_act"
          checked={hasDefenseBaseAct || false}
          onCheckedChange={(checked) => {
            setValue('has_defense_base_act', checked);
          }}
        />
      </div>
      
      {hasDefenseBaseAct && (
        <div className="space-y-2 pl-5 border-l-2 border-primary/20">
          <Label htmlFor="defense_base_act_details">Defense Base Act Exposure Details</Label>
          <Textarea
            id="defense_base_act_details"
            {...register('defense_base_act_details')}
            placeholder="Please describe the work performed on U.S. military bases outside the United States, including locations, duration of assignments, types of work, and safety protocols..."
          />
        </div>
      )}

      {/* Foreign Travel */}
      <div className="flex items-center justify-between">
        <Label htmlFor="has_foreign_travel" className="flex-1">
          Does the organization have employees who travel internationally for work?
        </Label>
        <Switch
          id="has_foreign_travel"
          checked={hasForeignTravel || false}
          onCheckedChange={(checked) => {
            setValue('has_foreign_travel', checked);
          }}
        />
      </div>
      
      {hasForeignTravel && (
        <div className="space-y-4 pl-5 border-l-2 border-primary/20">
          <div className="space-y-2">
            <Label htmlFor="foreign_travel_details">Foreign Travel Details</Label>
            <Textarea
              id="foreign_travel_details"
              {...register('foreign_travel_details')}
              placeholder="Describe the countries visited, frequency of travel, purpose of trips, and any travel safety protocols..."
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="annual_foreign_trips">Annual Number of Foreign Trips</Label>
              <Input
                id="annual_foreign_trips"
                {...register('annual_foreign_trips')}
                type="number"
                placeholder="Enter number of trips per year"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="avg_trip_duration">Average Trip Duration (days)</Label>
              <Input
                id="avg_trip_duration"
                {...register('avg_trip_duration')}
                type="number"
                placeholder="Enter average duration in days"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="max_employees_per_trip">Maximum Employees Per Trip</Label>
            <Input
              id="max_employees_per_trip"
              {...register('max_employees_per_trip')}
              type="number"
              placeholder="Enter maximum number of employees"
            />
          </div>
        </div>
      )}
      
      {/* Heights Work */}
      <div className="flex items-center justify-between">
        <Label htmlFor="has_heights_work" className="flex-1">
          Does the organization have employees who work at heights?
        </Label>
        <Switch
          id="has_heights_work"
          checked={hasHeightsWork || false}
          onCheckedChange={(checked) => {
            setValue('has_heights_work', checked);
          }}
        />
      </div>
      
      {hasHeightsWork && (
        <div className="space-y-4 pl-5 border-l-2 border-primary/20">
          <div className="space-y-2">
            <Label htmlFor="heights_work_details">Heights Work Description</Label>
            <Textarea
              id="heights_work_details"
              {...register('heights_work_details')}
              placeholder="Describe the work performed at heights, equipment used, and safety protocols..."
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="max_height">Maximum Working Height (in feet)</Label>
            <Input
              id="max_height"
              {...register('max_height')}
              type="text"
              placeholder="Enter the maximum working height"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="heights_safety_measures">Heights Safety Measures</Label>
            <Textarea
              id="heights_safety_measures"
              {...register('heights_safety_measures')}
              placeholder="Describe the safety measures for working at heights, including fall protection, training, and equipment..."
            />
          </div>
        </div>
      )}

      {/* Below Ground Work */}
      <div className="flex items-center justify-between">
        <Label htmlFor="has_below_ground_work" className="flex-1">
          Does the organization have employees who work below ground level?
        </Label>
        <Switch
          id="has_below_ground_work"
          checked={hasBelowGroundWork || false}
          onCheckedChange={(checked) => {
            setValue('has_below_ground_work', checked);
          }}
        />
      </div>
      
      {hasBelowGroundWork && (
        <div className="space-y-4 pl-5 border-l-2 border-primary/20">
          <div className="space-y-2">
            <Label htmlFor="below_ground_work_details">Below Ground Work Description</Label>
            <Textarea
              id="below_ground_work_details"
              {...register('below_ground_work_details')}
              placeholder="Describe the work performed below ground, excavation depth, and safety procedures..."
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="max_depth">Maximum Depth (in feet)</Label>
            <Input
              id="max_depth"
              {...register('max_depth')}
              type="text"
              placeholder="Enter the maximum depth"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confined_space_procedures">Confined Space Entry Procedures</Label>
            <Textarea
              id="confined_space_procedures"
              {...register('confined_space_procedures')}
              placeholder="Describe confined space entry procedures, permits, monitoring, and emergency protocols..."
            />
          </div>
        </div>
      )}

      {/* Hazardous Materials */}
      <div className="flex items-center justify-between">
        <Label htmlFor="has_hazardous_materials" className="flex-1">
          Does the organization handle hazardous materials?
        </Label>
        <Switch
          id="has_hazardous_materials"
          checked={hasHazardousMaterials || false}
          onCheckedChange={(checked) => {
            setValue('has_hazardous_materials', checked);
          }}
        />
      </div>
      
      {hasHazardousMaterials && (
        <div className="space-y-2 pl-5 border-l-2 border-primary/20">
          <Label htmlFor="hazardous_materials_details">Hazardous Materials Details</Label>
          <Textarea
            id="hazardous_materials_details"
            {...register('hazardous_materials_details')}
            placeholder="Describe the hazardous materials handled, storage methods, handling procedures, training programs, and safety protocols..."
          />
        </div>
      )}
    </div>
  );
};
